import { css, SerializedStyles } from '@emotion/react';
import { media } from './breakpoints';

export const fontFamilies = {
    sans: "'Zalando Sans', Arial, Helvetica, sans-serif",
    expanded: "'Zalando Sans Expanded', 'Zalando Sans', Arial, sans-serif",
} as const;

type TextVariant = {
    size: number;
    lineHeight: string;
    letterSpacing: string;
};

type TextStyle = TextVariant & {
    family: keyof typeof fontFamilies;
    weight: number;
    mobile?: Partial<TextVariant>;
    uppercase?: boolean;
    underline?: boolean;
};

const variantCss = ({
    size,
    lineHeight,
    letterSpacing,
}: Partial<TextVariant>) => css`
    ${size !== undefined && `font-size: ${size}px;`}
    ${lineHeight !== undefined && `line-height: ${lineHeight};`}
    ${letterSpacing !== undefined && `letter-spacing: ${letterSpacing};`}
`;

const toCss = ({
    family,
    weight,
    mobile,
    uppercase,
    underline,
    ...desktop
}: TextStyle): SerializedStyles => css`
    font-family: ${fontFamilies[family]};
    font-weight: ${weight};
    ${variantCss(desktop)}
    ${uppercase && 'text-transform: uppercase;'}
    ${underline && 'text-decoration-line: underline;'}
    ${mobile &&
    css`
        ${media.mobile} {
            ${variantCss(mobile)}
        }
    `}
`;

const AUTO = 'normal';

export const typography = {
    tertiary: toCss({
        family: 'expanded',
        weight: 600,
        size: 18,
        lineHeight: AUTO,
        letterSpacing: '-0.01em',
        uppercase: true,
        mobile: { size: 16, letterSpacing: '-0.02em' },
    }),
    headingXl: toCss({
        family: 'expanded',
        weight: 900,
        size: 36,
        lineHeight: AUTO,
        letterSpacing: '0',
        mobile: { size: 28 },
    }),
    headingL: toCss({
        family: 'expanded',
        weight: 900,
        size: 34,
        lineHeight: AUTO,
        letterSpacing: '0',
        mobile: { size: 26 },
    }),
    subhead: toCss({
        family: 'sans',
        weight: 700,
        size: 24,
        lineHeight: AUTO,
        letterSpacing: '-0.02em',
        mobile: { size: 20, letterSpacing: '-0.03em' },
    }),
    bodyXl: toCss({
        family: 'sans',
        weight: 600,
        size: 18,
        lineHeight: '24px',
        letterSpacing: '-0.02em',
    }),
    bodyL: toCss({
        family: 'sans',
        weight: 600,
        size: 16,
        lineHeight: '24px',
        letterSpacing: '-0.03em',
    }),
    bodyMd: toCss({
        family: 'sans',
        weight: 500,
        size: 16,
        lineHeight: '24px',
        letterSpacing: '-0.03em',
    }),
    bodySm: toCss({
        family: 'sans',
        weight: 500,
        size: 15,
        lineHeight: '24px',
        letterSpacing: '-0.03em',
    }),
    bodyXs: toCss({
        family: 'sans',
        weight: 400,
        size: 15,
        lineHeight: '24px',
        letterSpacing: '-0.03em',
    }),
    caption: toCss({
        family: 'sans',
        weight: 500,
        size: 13,
        lineHeight: '18px',
        letterSpacing: '-0.03em',
        mobile: { size: 12, lineHeight: '17px' },
    }),
    buttonMd: toCss({
        family: 'sans',
        weight: 700,
        size: 16,
        lineHeight: '24px',
        letterSpacing: '-0.02em',
    }),
    buttonLg: toCss({
        family: 'sans',
        weight: 700,
        size: 18,
        lineHeight: '24px',
        letterSpacing: '-0.02em',
        mobile: { size: 16 },
    }),
    linkXl: toCss({
        family: 'sans',
        weight: 600,
        size: 16,
        lineHeight: AUTO,
        letterSpacing: '-0.03em',
        underline: true,
    }),
    linkMd: toCss({
        family: 'sans',
        weight: 600,
        size: 15,
        lineHeight: AUTO,
        letterSpacing: '-0.03em',
        underline: true,
    }),
    linkSm: toCss({
        family: 'sans',
        weight: 500,
        size: 14,
        lineHeight: AUTO,
        letterSpacing: '-0.03em',
        underline: true,
    }),
} as const;
